import { ChevronRightIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { Link } from 'react-router-dom';

interface IRecordSectionLink {
  label: string;
  to: string;
}

interface IRecordSectionProps {
  title: string;
  /** Trailing "View merchant ›" style link in the section header. */
  link?: IRecordSectionLink;
  /** The card body. Direct children are separated by hairline dividers. */
  children: React.ReactNode;
  className?: string;
}

/**
 * A titled block in a record page's body (Figma "Transaction/Card/*"): a
 * header row — title, optional trailing link — above a white card whose
 * direct children are divided by hairlines. Sections stack with the pane's
 * 20px gap; the card carries no inner padding so each row owns its own.
 */
const RecordSection = ({ title, link, children, className }: IRecordSectionProps) => {
  return (
    <section className={cx('flex w-full flex-col gap-3', className)}>
      <div className="flex items-center justify-between gap-2 px-1">
        <h2 className="text-primary min-w-0 truncate text-lg font-semibold">{title}</h2>
        {link && (
          <Link
            to={link.to}
            className="text-brand-secondary hover:text-brand-secondary_hover text-reg flex shrink-0 items-center gap-0.5 font-medium leading-5"
          >
            {link.label}
            <ChevronRightIcon aria-hidden="true" className="size-3.5 shrink-0" />
          </Link>
        )}
      </div>
      <div className="bg-primary divide-tertiary flex w-full flex-col divide-y overflow-hidden rounded-2xl shadow-sm">
        {children}
      </div>
    </section>
  );
};

export default RecordSection;
